import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import MindfullLogo from '@/components/MindfullLogo';
import { FolderFloat, type FolderFloatEntry } from '@/components/items/folder/FolderFloat';
import { getFolderColorById } from '@/lib/folderColors';
import { cn } from '@/lib/utils';
import { CardFace, CARD_LABEL, type CardKind } from './cards';
import { WaitlistForm } from './WaitlistForm';

/**
 * Section one: the hero is a live board. The headline sits on the canvas dot
 * grid with the things people save scattered around it. On a mouse or
 * trackpad every card drags (brand spring, momentum, kept inside the
 * section), the folder fans open on hover like it does in the app, and the
 * board leans a few pixels toward the pointer. Touch gets the same board
 * without drag so the page still scrolls. Reduced motion: no entrance, no
 * parallax.
 */

type Pos = { l: string; t: string };

interface Piece {
  id: string;
  kind: CardKind | 'folder';
  rot: number;
  /** Parallax depth, 0..1. */
  depth: number;
  lg: Pos;
  md?: Pos;
  sm?: Pos;
}

// Percent of the section box. From 1280px wide (xl) all ten cards ring the
// copy; tablets and small laptops keep a band above and below it, phones the
// same band with cards bleeding off the edges.
const PIECES: Piece[] = [
  { id: 'sticky', kind: 'sticky', rot: -6, depth: 0.9, lg: { l: '6%', t: '15%' }, md: { l: '4%', t: '12%' }, sm: { l: '-3%', t: '12%' } },
  { id: 'link', kind: 'link', rot: 3, depth: 0.5, lg: { l: '24%', t: '9%' }, md: { l: '20%', t: '9%' }, sm: { l: '30%', t: '10.5%' } },
  { id: 'voice', kind: 'voice', rot: -3, depth: 0.7, lg: { l: '58%', t: '12%' } },
  { id: 'chair', kind: 'chair', rot: 6, depth: 1, lg: { l: '81%', t: '11%' }, md: { l: '76%', t: '10%' }, sm: { l: '73%', t: '12%' } },
  { id: 'quote', kind: 'quote', rot: -4, depth: 0.6, lg: { l: '1.5%', t: '46%' } },
  { id: 'palette', kind: 'palette', rot: 4, depth: 0.8, lg: { l: '85%', t: '45%' } },
  { id: 'fjord', kind: 'fjord', rot: 4, depth: 0.7, lg: { l: '6%', t: '73%' }, md: { l: '2%', t: '76%' }, sm: { l: '-8%', t: '84%' } },
  { id: 'todo', kind: 'todo', rot: -4, depth: 1, lg: { l: '26%', t: '80%' }, md: { l: '34%', t: '82%' } },
  { id: 'pdf', kind: 'pdf', rot: -7, depth: 0.6, lg: { l: '64%', t: '77%' }, md: { l: '62%', t: '78%' }, sm: { l: '40%', t: '86%' } },
  { id: 'folder', kind: 'folder', rot: 3, depth: 0.85, lg: { l: '80%', t: '74%' }, md: { l: '80%', t: '80%' }, sm: { l: '74%', t: '85%' } },
];

const HEADLINE = [
  { words: ['A', 'place', 'for', 'everything'], muted: false },
  { words: ['on', 'your', 'mind.'], muted: true },
];

const FOLDER_PAPERS: FolderFloatEntry[] = [
  { key: 'tiles', label: 'Terrazzo tiles' },
  { key: 'lamp', label: 'Paper lamp' },
  { key: 'sink', label: 'Sink, brushed steel' },
  { key: 'oak', label: 'Oak sample' },
  { key: 'rug', label: 'Wool rug' },
];

function useFinePointer() {
  const [fine, setFine] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)');
    const update = () => setFine(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return fine;
}

function HeroFolder() {
  const sage = getFolderColorById('sage');
  return (
    <FolderFloat
      items={FOLDER_PAPERS}
      label="Kitchen"
      sublabel="14 items"
      trigger="hover"
      folderColor={sage.folderDark}
      frontColor={sage.folder}
      paperColor={sage.label}
      itemColor="hsl(var(--paper-raised))"
      itemTextColor="hsl(var(--ink))"
      labelColor={sage.labelText}
      width={176}
      height={130}
      radius={14}
      spread={170}
      lift={24}
      openDelay={60}
    />
  );
}

interface FloatingProps {
  piece: Piece;
  index: number;
  z: number;
  draggable: boolean;
  still: boolean;
  bounds: React.RefObject<HTMLElement>;
  leanX: MotionValue<number>;
  leanY: MotionValue<number>;
  onLift: (id: string) => void;
}

function Floating({ piece, index, z, draggable, still, bounds, leanX, leanY, onLift }: FloatingProps) {
  const x = useTransform(leanX, (v) => v * piece.depth * 14);
  const y = useTransform(leanY, (v) => v * piece.depth * 10);
  const { lg, md, sm } = piece;

  return (
    <div
      className={cn(
        'absolute',
        sm ? 'block' : md ? 'hidden sm:block' : 'hidden xl:block',
        'left-[var(--l-sm)] top-[var(--t-sm)] sm:left-[var(--l-md)] sm:top-[var(--t-md)] xl:left-[var(--l-lg)] xl:top-[var(--t-lg)]',
      )}
      style={
        {
          zIndex: z,
          '--l-sm': sm?.l,
          '--t-sm': sm?.t,
          '--l-md': (md ?? lg).l,
          '--t-md': (md ?? lg).t,
          '--l-lg': lg.l,
          '--t-lg': lg.t,
        } as React.CSSProperties
      }
    >
      <motion.div style={still ? undefined : { x, y }}>
        <motion.div
          role="img"
          aria-label={piece.kind === 'folder' ? 'Folder: Kitchen, 14 items' : CARD_LABEL[piece.kind]}
          drag={draggable}
          dragConstraints={bounds}
          dragElastic={0.12}
          dragTransition={{ power: 0.25, timeConstant: 220, bounceStiffness: 500, bounceDamping: 38 }}
          onDragStart={() => onLift(piece.id)}
          onPointerDown={() => draggable && onLift(piece.id)}
          initial={still ? false : { opacity: 0, y: -28, scale: 1.08, rotate: piece.rot - 8 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: piece.rot }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 22,
            delay: 0.35 + index * 0.07,
            opacity: { duration: 0.2, delay: 0.35 + index * 0.07 },
          }}
          whileHover={draggable ? { scale: 1.025 } : undefined}
          whileDrag={{ scale: 1.05, rotate: 0, transition: { type: 'spring', stiffness: 500, damping: 38 } }}
          className={cn(
            'origin-center',
            draggable && 'cursor-grab active:cursor-grabbing',
            'touch-pan-y',
          )}
        >
          {/* zoom keeps the drag hit area the size of what you see */}
          <div className="[zoom:0.62] sm:[zoom:0.78] lg:[zoom:0.86] xl:[zoom:0.9] min-[1400px]:[zoom:1]">
            {piece.kind === 'folder' ? <HeroFolder /> : <CardFace kind={piece.kind} />}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

function DotGrid() {
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden>
      <defs>
        <pattern id="hero-dots" width="22" height="22" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.1" fill="hsl(var(--canvas-grid))" opacity="0.5" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#hero-dots)" />
    </svg>
  );
}

export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion() ?? false;
  const fine = useFinePointer();
  const [order, setOrder] = useState<string[]>(() => PIECES.map((p) => p.id));

  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const leanX = useSpring(pointerX, { stiffness: 120, damping: 30 });
  const leanY = useSpring(pointerY, { stiffness: 120, damping: 30 });

  const lift = (id: string) => setOrder((o) => (o[o.length - 1] === id ? o : [...o.filter((x) => x !== id), id]));

  const onPointerMove = (e: React.PointerEvent) => {
    if (reduced || !fine) return;
    pointerX.set((e.clientX / window.innerWidth - 0.5) * 2);
    pointerY.set((e.clientY / window.innerHeight - 0.5) * 2);
  };

  return (
    <section
      ref={ref}
      onPointerMove={onPointerMove}
      aria-labelledby="hero-title"
      className="relative flex min-h-[max(100svh,640px)] flex-col overflow-x-clip bg-paper lg:min-h-[max(100svh,760px)]"
    >
      <DotGrid />

      <nav className="relative z-40 flex items-center justify-between px-5 py-5 sm:px-8 lg:px-12">
        <Link to="/" aria-label="Mindfull home" className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal">
          <MindfullLogo size="md" animated />
        </Link>
        <div className="flex items-center gap-1 sm:gap-2">
          <Link
            to="/login"
            className="rounded-full px-4 py-2 text-[14px] font-medium text-ink-2 transition-colors duration-150 hover:bg-wash hover:text-ink"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="rounded-full bg-ink px-4 py-2 text-[14px] font-medium text-paper-raised transition-transform duration-150 ease-out active:scale-[0.97] sm:px-5"
          >
            Get early access
          </Link>
        </div>
      </nav>

      {PIECES.map((piece, i) => (
        <Floating
          key={piece.id}
          piece={piece}
          index={i}
          z={10 + order.indexOf(piece.id)}
          draggable={fine}
          still={reduced}
          bounds={ref}
          leanX={leanX}
          leanY={leanY}
          onLift={lift}
        />
      ))}

      <div className="pointer-events-none relative z-30 flex flex-1 flex-col items-center justify-center px-5 pb-28 pt-40 text-center sm:pb-32 sm:pt-44 lg:pb-24 lg:pt-12">
        <p className="mb-6 font-mono text-[12px] uppercase tracking-[0.08em] text-ink-3">Now in private beta</p>
        <h1
          id="hero-title"
          className="font-display text-[clamp(2.6rem,6.4vw,6.5rem)] font-semibold leading-[0.98] tracking-[-0.035em] text-ink"
        >
          {HEADLINE.map((line, li) => (
            <span key={li} className={cn('block', line.muted && 'text-ink-3')}>
              {line.words.map((w, wi) => {
                const n = li * 4 + wi;
                return (
                  <span key={wi} className="inline-block overflow-hidden pb-[0.08em] align-top">
                    <motion.span
                      className="inline-block"
                      initial={reduced ? false : { y: '105%' }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.08 + n * 0.055 }}
                    >
                      {w}
                    </motion.span>
                    {wi < line.words.length - 1 ? ' ' : null}
                  </span>
                );
              })}
            </span>
          ))}
        </h1>
        <motion.p
          initial={reduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
          className="mt-7 max-w-[34rem] text-[17px] leading-[27px] text-ink-2 sm:text-lg sm:leading-[28px]"
        >
          Save the links, screenshots, notes and half-ideas you keep losing. They live on one calm
          canvas, and Partnr finds them again when you ask.
        </motion.p>
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.68 }}
          className="pointer-events-auto mt-9 w-full max-w-[27rem]"
        >
          <WaitlistForm id="hero-email" />
        </motion.div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 hidden items-end justify-between px-5 pb-5 font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3 sm:flex sm:px-8 lg:px-12">
        <span>{fine ? 'Everything here moves. Try it.' : 'A calm place to keep things'}</span>
        <span className="flex items-center gap-2">
          Scroll
          <span className="relative block h-6 w-px overflow-hidden bg-line">
            {!reduced && <span className="absolute inset-x-0 top-0 h-2 animate-[hero-scroll_1.8s_cubic-bezier(.22,1,.36,1)_infinite] bg-ink-3" />}
          </span>
        </span>
      </div>
    </section>
  );
}
